import {
  DataGrid,
  GridToolbar,
  GridColDef,
  useGridApiRef,
} from '@mui/x-data-grid';

import {
  Box,
  Paper,
  Button,
  Typography
} from '@mui/material';

import {
  useEffect,
  useState
} from 'react';

import axios from 'axios';
import DownloadIcon from '@mui/icons-material/Download';
import { apiString as API_URL } from '../../utils/constants';

interface ScoringDto {

  ObjectMember_id: string;

  FirstName: string;

  LastName: string;

  Total_Score: number;

  Lambda: number;

  InsuranceType: string;

  Probability: number;

  DateModel: string;

  DateOfOnboard: string;

  Teams: string;

  CCTeam: string;

  BHTeam: string;

  NPTeam: string;

  CLTeam: string;

  CHWTeam: string;
}


const getScoreColor = (score: number) => {

  if (score > 0.45)
    return '#ff4d4f';

  if (score > -2.0)
    return '#fa8c16';

  if (score > -6.3)
    return '#fadb14';

  return '#52c41a';
};

const parseGridDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatGridDate = (value: string | Date | null | undefined): string => {
  const date = parseGridDate(value);
  if (!date) return '';
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  return `${month}/${day}/${year}`;
};

const columns: GridColDef[] = [
  {
    field: 'LastName',
    headerName: 'Last Name',
    minWidth: 150,
    flex: 1
  },

  {
    field: 'FirstName',
    headerName: 'First Name',
    minWidth: 150,
    flex: 1
  },

  {
    field: 'HealthPlan',
    headerName: 'Health Plan',
    minWidth: 150,
    flex: 1
  },

  {
    field: 'Population',
    headerName: 'Population',
    minWidth: 150,
    flex: 1
  },

  {
    field: 'Total_Score',

    headerName: 'Score',

    width: 130,

    type: 'number',
    align: 'center',
    headerAlign: 'center',

    sortable: true,

    renderCell: (params) => {

      const score = params.value as number;

      return (

        <Box
          sx={{

            width: '100%',

            height: '80%',

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'center',

            borderRadius: '8px',

            fontWeight: 'bold',

            color:
              score > -6.3 && score <= -2.0
                ? 'black'
                : 'white',

            backgroundColor:
              getScoreColor(score)
          }}
        >
          {score?.toFixed(2)}
        </Box>
      );
    }
  },

  {
    field: 'Probability',

    headerName: 'Probability',

    width: 130,

    type: 'number',
    align: 'center',
    headerAlign: 'center',

    sortable: true
  },

  {
    field: 'DateOfOnboard',

    headerName: 'Onboard Date',

    width: 150,

    type: 'date',

    sortable: true,

    valueGetter: parseGridDate,
    valueFormatter: formatGridDate
  },


  {
    field: 'DateModel',

    headerName: 'Scoring Date',

    width: 150,

    type: 'date',

    sortable: true,

    valueGetter: parseGridDate,
    valueFormatter: formatGridDate
  },

  {
    field: 'Teams',

    headerName: 'Team',

    minWidth: 200,
    flex: 1
  },
];

export default function DashRTCS() {
  const apiRef = useGridApiRef();

  const [downLoading, setDownLoading] = useState<boolean>(false);

  const [rows, setRows] = useState<
    (ScoringDto & { id: string })[]
  >([]);


  useEffect(() => {
    axios
      .get<ScoringDto[]>(
        `${API_URL}/scoring/GetSP`
      )
      .then(res => {
        const dataWithId = res.data.map(
          (item, index) => ({

            id: `${item.ObjectMember_id}-${index}`,

            ...item
          })
        );

        setRows(dataWithId);

      })
      .catch(err => console.error(err));

  }, []);


  const exportData = async () => {
    try {
        setDownLoading(true);

        const response = await axios.get(
            `${API_URL}/scoring/export`
        );

        const base64String = response.data.base64String;

        const byteCharacters = atob(base64String);

        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob(
            [byteArray],
            {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        );

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');

        const date = new Date();

        const mm = String(
            date.getMonth() + 1
        ).padStart(2, '0');

        const dd = String(
            date.getDate()
        ).padStart(2, '0');

        const yyyy = date.getFullYear();

        link.href = url;

        link.download =
            `ScoringMembers - ${mm}${dd}${yyyy}.xlsx`;

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    }
    catch (err) {
        console.error(
            'Error exporting scoring data:',
            err
        );
    }
    finally {
        setDownLoading(false);
    }
};

  return (

    <Paper
      elevation={5}
      sx={{ m: { xs: '8px', sm: '24px' }, p: '16px', minWidth: 0 }}
    >

      {/* HEADER */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
          alignItems: 'center',
          gap: 2
        }}
      >
        <Typography
          variant="h6"
          color="primary"
          sx={{ gridColumn: { xs: 1, md: 2 }, textAlign: 'center' }}
        >
          Refuse To Continue Service Members Scoring
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => exportData()}
          sx={{ justifySelf: 'end', gridColumn: { xs: 1, md: 3 } }}
          disabled={downLoading || rows?.length === 0}
        >
          {downLoading ? 'Exporting...' : 'Export'}
        </Button>
      </Box>
      {/* TABLE */}
      <Box
        style={{
          height: 700,
          width: '100%',
          marginTop: 20,
          minWidth: 0
        }}
      >

        <DataGrid
          apiRef={apiRef}

          rows={rows}

          columns={columns}

          pageSizeOptions={[
            5,
            10,
            20,
            50
          ]}

          disableRowSelectionOnClick


          sortingOrder={[
            'asc',
            'desc'
          ]}

          sortingMode="client"


          filterMode="client"


          slots={{
            toolbar: GridToolbar
          }}


          slotProps={{
            toolbar: {
              showQuickFilter: true
            }
          }}

          initialState={{

            pagination: {

              paginationModel: {
                pageSize: 10
              }
            }
          }}

          sx={{

            border: 0,

            '& .MuiDataGrid-columnHeaders': {

              backgroundColor: '#f5f5f5',

              fontWeight: 'bold'
            }
          }}
        />

      </Box>

    </Paper>
  );
}